import React, { useState, useCallback, useRef, useEffect } from 'react'

/**
 * ImageResizeHandles Component
 * 
 * Provides circular drag handles around selected images for resizing functionality.
 * Supports 8 handles (4 corners + 4 midpoints) with aspect ratio preservation.
 */
const ImageResizeHandles = ({ 
  imageElement, 
  onResize, 
  onResizeComplete,
  isVisible = true 
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragHandle, setDragHandle] = useState(null)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [startSize, setStartSize] = useState({ width: 0, height: 0 })
  const [currentSize, setCurrentSize] = useState({ width: 0, height: 0 })
  
  // Handle positions: nw, n, ne, e, se, s, sw, w
  const handles = [
    { id: 'nw', cursor: 'nw-resize', position: 'top-left' },
    { id: 'n', cursor: 'n-resize', position: 'top-center' },
    { id: 'ne', cursor: 'ne-resize', position: 'top-right' },
    { id: 'e', cursor: 'e-resize', position: 'middle-right' },
    { id: 'se', cursor: 'se-resize', position: 'bottom-right' },
    { id: 's', cursor: 's-resize', position: 'bottom-center' },
    { id: 'sw', cursor: 'sw-resize', position: 'bottom-left' },
    { id: 'w', cursor: 'w-resize', position: 'middle-left' }
  ]
  
  // Initialize current size from image element
  useEffect(() => {
    if (imageElement) {
      const rect = imageElement.getBoundingClientRect()
      setCurrentSize({ width: rect.width, height: rect.height })
    }
  }, [imageElement])
  
  /**
   * Handle mouse down on resize handle
   */
  const handleMouseDown = useCallback((e, handleId) => {
    e.preventDefault()
    e.stopPropagation()
    
    setIsDragging(true)
    setDragHandle(handleId)
    
    const rect = imageElement.getBoundingClientRect()
    setStartPos({ x: e.clientX, y: e.clientY })
    setStartSize({ width: rect.width, height: rect.height })
    
    // Set cursor for the entire document during drag
    document.body.style.cursor = handles.find(h => h.id === handleId)?.cursor || 'default'
    document.body.style.userSelect = 'none'
  }, [imageElement, handles])
  
  /**
   * Handle mouse move during drag
   */
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragHandle) return
    
    const deltaX = e.clientX - startPos.x
    const deltaY = e.clientY - startPos.y
    
    let newWidth = startSize.width
    let newHeight = startSize.height
    const aspectRatio = startSize.width / startSize.height
    
    // Calculate new dimensions based on handle being dragged
    switch (dragHandle) {
      case 'nw':
        newWidth = startSize.width - deltaX
        newHeight = startSize.height - deltaY
        break
      case 'n':
        newHeight = startSize.height - deltaY
        newWidth = newHeight * aspectRatio
        break
      case 'ne':
        newWidth = startSize.width + deltaX
        newHeight = startSize.height - deltaY
        break
      case 'e':
        newWidth = startSize.width + deltaX
        newHeight = newWidth / aspectRatio
        break
      case 'se':
        newWidth = startSize.width + deltaX
        newHeight = startSize.height + deltaY
        break
      case 's':
        newHeight = startSize.height + deltaY
        newWidth = newHeight * aspectRatio
        break
      case 'sw':
        newWidth = startSize.width - deltaX
        newHeight = startSize.height + deltaY
        break
      case 'w':
        newWidth = startSize.width - deltaX
        newHeight = newWidth / aspectRatio
        break
    }
    
    // Maintain aspect ratio for corner handles
    if (['nw', 'ne', 'se', 'sw'].includes(dragHandle)) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        newHeight = newWidth / aspectRatio
      } else {
        newWidth = newHeight * aspectRatio
      }
    }
    
    // Apply minimum and maximum constraints
    newWidth = Math.max(50, Math.min(800, newWidth))
    newHeight = Math.max(50, Math.min(600, newHeight))
    
    setCurrentSize({ width: newWidth, height: newHeight })
    
    // Apply resize to image element
    if (imageElement) {
      imageElement.style.width = `${newWidth}px`
      imageElement.style.height = `${newHeight}px`
    }
    
    if (onResize) {
      onResize(newWidth, newHeight)
    }
  }, [isDragging, dragHandle, startPos, startSize, imageElement, onResize])
  
  /**
   * Handle mouse up - complete resize
   */
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      setDragHandle(null)
      
      // Reset cursor and selection
      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
      
      if (onResizeComplete) {
        onResizeComplete(currentSize.width, currentSize.height)
      }
    }
  }, [isDragging, currentSize, onResizeComplete])
  
  /**
   * Handle touch start for mobile support
   */
  const handleTouchStart = useCallback((e, handleId) => {
    e.preventDefault()
    e.stopPropagation()
    
    const touch = e.touches[0]
    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation()
    }
    
    handleMouseDown(mouseEvent, handleId)
  }, [handleMouseDown])
  
  /**
   * Handle touch move for mobile support
   */
  const handleTouchMove = useCallback((e) => {
    e.preventDefault()
    
    const touch = e.touches[0]
    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY
    }
    
    handleMouseMove(mouseEvent)
  }, [handleMouseMove])
  
  /**
   * Handle touch end for mobile support
   */
  const handleTouchEnd = useCallback((e) => {
    e.preventDefault()
    handleMouseUp()
  }, [handleMouseUp])
  
  // Add global mouse event listeners during drag
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e)
      const handleGlobalMouseUp = (e) => handleMouseUp(e)
      const handleGlobalTouchMove = (e) => handleTouchMove(e)
      const handleGlobalTouchEnd = (e) => handleTouchEnd(e)
      
      document.addEventListener('mousemove', handleGlobalMouseMove)
      document.addEventListener('mouseup', handleGlobalMouseUp)
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false })
      document.addEventListener('touchend', handleGlobalTouchEnd)
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove)
        document.removeEventListener('mouseup', handleGlobalMouseUp)
        document.removeEventListener('touchmove', handleGlobalTouchMove)
        document.removeEventListener('touchend', handleGlobalTouchEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd])
  
  if (!isVisible || !imageElement) return null
  
  /**
   * Get positioning styles for each handle
   */
  const getHandleStyle = (handle) => {
    const baseStyle = {
      position: 'absolute',
      width: '12px',
      height: '12px',
      backgroundColor: '#3b82f6',
      border: '2px solid white',
      borderRadius: '50%',
      cursor: handle.cursor,
      zIndex: 1000,
      transform: 'translate(-50%, -50%)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      transition: isDragging ? 'none' : 'transform 0.15s ease, box-shadow 0.15s ease'
    }
    
    // Position handles around the image
    switch (handle.id) {
      case 'nw':
        return { ...baseStyle, top: '-6px', left: '-6px' }
      case 'n':
        return { ...baseStyle, top: '-6px', left: '50%' }
      case 'ne':
        return { ...baseStyle, top: '-6px', right: '-6px', left: 'auto', transform: 'translate(50%, -50%)' }
      case 'e':
        return { ...baseStyle, top: '50%', right: '-6px', left: 'auto', transform: 'translate(50%, -50%)' }
      case 'se':
        return { ...baseStyle, bottom: '-6px', right: '-6px', top: 'auto', left: 'auto', transform: 'translate(50%, 50%)' }
      case 's':
        return { ...baseStyle, bottom: '-6px', left: '50%', top: 'auto', transform: 'translate(-50%, 50%)' }
      case 'sw':
        return { ...baseStyle, bottom: '-6px', left: '-6px', top: 'auto', transform: 'translate(-50%, 50%)' }
      case 'w':
        return { ...baseStyle, top: '50%', left: '-6px' }
      default:
        return baseStyle
    }
  }
  
  return (
    <>
      {handles.map((handle) => (
        <div
          key={handle.id}
          style={getHandleStyle(handle)}
          onMouseDown={(e) => handleMouseDown(e, handle.id)}
          onTouchStart={(e) => handleTouchStart(e, handle.id)}
          className="resize-handle hover:scale-125 transition-transform duration-150"
          title={`Resize ${handle.position}`}
        />
      ))}
      
      {/* Resize preview overlay */}
      {isDragging && (
        <div
          className="absolute pointer-events-none border-2 border-dashed border-blue-400 bg-blue-50/20 dark:bg-blue-900/20"
          style={{
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 999
          }}
        />
      )}
    </>
  )
}

export default ImageResizeHandles
